import { useData } from "../../../../runtime/provider";
import { useCallback, useEffect, useRef } from "react";

const DEFAULT_SETTLE_MS = 500;

// Mirrors `requestPublishBatchInputSchema` on the API
// (`contexts/audio-publish/server/src/api/catalog/songs/requestPublish.ts`): `songIds` is
// `.min(1).max(100)`. This is a sanity bound on the wire call, not a
// business cap on how many songs may be prewarmed — callers can pass
// arbitrarily long lists; the hook chunks internally.
const MAX_BATCH_SIZE = 100;

export type PrewarmableSong = { id: string; status: string };

export type UsePrewarmSongsOptions = {
  settleMs?: number;
  requestPublishBatch?: (songIds: string[]) => Promise<unknown>;
};

const chunk = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

/**
 * Given the currently-visible list of songs, fires a best-effort batch
 * `requestPublish` kick for the internal ones, in display/ranking order,
 * once the list has been stable for `settleMs` (default 500ms) — so rapid
 * typing/scrolling/filtering doesn't spam the server with a request per
 * intermediate render.
 *
 * Wired into SEARCH results only (`ExploreScreen`), deliberately: search is
 * the one surface with no server-side read-path intent hook (a hook there
 * would fire per keystroke), so this settle-fire is its only pre-tap
 * warmer. Album/playlist/artist screens do NOT use this — their backend
 * reads (`album-open`/`playlist-open`/`artist-*` in
 * `apps/api/.../publish-intent.ts`) already fired intents for the exact
 * same songs when the data was fetched, and a cached re-render implies a
 * previous fetch that already fired; re-sending the list from the client
 * would be pure duplicate traffic.
 *
 * This is a PREWARM, not the play-time gate: a hint that these songs are
 * likely to be tapped soon, so publishing (if not already in flight) can
 * start before the tap. By the time a user actually taps (e.g. ~1.5–3s
 * after search results settle), the play-time poll gate's first tick is
 * far more likely to hit 200 immediately — a search tap starts feeling
 * like an album tap. The play-time poll+rescue in
 * `ensureSongPlayable`/`pollForPlayableAudio` remains the unconditional
 * source of truth and backstop regardless of whether this hook ran; this
 * is purely a latency optimization.
 *
 * De-dupes for the component's lifetime via an in-memory `Set` — NOT a TTL
 * policy (the server has no client-side dedup of its own by design; this
 * only suppresses render noise so the same song isn't re-sent on every
 * unrelated rerender while it's still on screen). Unmounting and
 * remounting the component (e.g. closing and reopening search) resets it.
 *
 * Swallows all errors, never awaited by the caller, never blocks
 * rendering, never throws.
 */
export function usePrewarmSongs(
  songs: PrewarmableSong[],
  options?: UsePrewarmSongsOptions,
): void {
  const data = useData();
  const requestBatch = useCallback(
    (songIds: string[]) =>
      data.api.catalog.songs.requestPublishBatch.mutate({ songIds }),
    [data],
  );
  const settleMs = options?.settleMs ?? DEFAULT_SETTLE_MS;
  const requestPublishBatch = options?.requestPublishBatch ?? requestBatch;
  const sentRef = useRef<Set<string>>(new Set());

  // A joined-ids string (not the `songs`/filtered-array references, which
  // are new on every render) as the effect dependency — the settle timer
  // should only restart when the actual set of visible internal song ids
  // changes, not on every unrelated rerender. Safe to round-trip through
  // join/split: these are always UUIDs (see `requestPublishBatchInputSchema`
  // on the API), which never contain a comma.
  const idsKey = songs
    .filter((song) => song.status === "internal")
    .map((song) => song.id)
    .join(",");

  useEffect(() => {
    if (!data.isCurrent() || idsKey === "") return;
    const internalIds = idsKey.split(",");

    const timer = setTimeout(() => {
      if (!data.isCurrent()) return;
      const idsToSend = internalIds.filter((id) => !sentRef.current.has(id));
      if (idsToSend.length === 0) return;

      idsToSend.forEach((id) => sentRef.current.add(id));

      for (const batch of chunk(idsToSend, MAX_BATCH_SIZE)) {
        // Wrap in Promise.resolve().then(...) so a synchronous throw from
        // `requestPublishBatch` (bad mock, misconfigured client) is caught
        // the same way as an async rejection — never allowed to surface.
        Promise.resolve()
          .then(() => requestPublishBatch(batch))
          .catch(() => {
            // Swallow all errors — best-effort prewarm only.
          });
      }
    }, settleMs);

    const cancel = () => clearTimeout(timer);
    data.signal.addEventListener("abort", cancel, { once: true });
    return () => {
      data.signal.removeEventListener("abort", cancel);
      cancel();
    };
  }, [idsKey, settleMs, requestPublishBatch, data]);
}
